import React, { useState } from 'react';
import type { QuestDTO } from '@hexcrawl/shared-utils';
import { MapScene } from '@/MapScene';
import { useCheatStore } from '../../stores/cheatStore';
import { cheatButtonStyle } from '../../constants/cheatStyles';

type Tab = 'active' | 'completed' | 'failed';

interface Props {
  quests: QuestDTO[];
  onClose: () => void;
}

function navigateToHex(x: number, y: number) {
  const mapScene = window.phaserGame?.scene?.getScene('MapScene') as MapScene | undefined;
  mapScene?.centerOnHex(x, y);
}

function jumpToHex(x: number, y: number) {
  const mapScene = window.phaserGame?.scene?.getScene('MapScene') as MapScene | undefined;
  void mapScene?.jumpToHex(x, y);
}

const ActiveQuestEntry: React.FC<{
  quest: QuestDTO;
  expanded: boolean;
  cheatsEnabled: boolean;
  onToggle: () => void;
  onNavigate: () => void;
  onJump: () => void;
}> = ({ quest, expanded, cheatsEnabled, onToggle, onNavigate, onJump }) => {
  const isReadyToComplete = quest.status === 'ready_to_complete';
  const isCompanionQuest = !!quest.companionNpcId;
  const navHex = isReadyToComplete && !isCompanionQuest ? quest.questGiverHex : quest.targetHex;
  const hasLocation = navHex != null && !(isCompanionQuest && isReadyToComplete);

  return (
    <div
      onClick={onToggle}
      style={{
        padding: '10px 12px',
        marginBottom: 8,
        background: isReadyToComplete ? 'rgba(136, 255, 136, 0.07)' : 'rgba(22, 53, 74, 0.5)',
        border: `1px solid ${isReadyToComplete ? '#44aa44' : '#2a4a6a'}`,
        borderRadius: 6,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 'bold', fontSize: 13, color: '#d9ecff', fontFamily: 'monospace', flex: 1 }}>
          {quest.title}
        </span>
        {isReadyToComplete && (
          <span style={{ fontSize: 10, color: '#88ff88', fontFamily: 'monospace', fontWeight: 'bold' }}>
            {'✓ RETURN TO NPC'}
          </span>
        )}
        <span style={{ fontSize: 11, color: '#446688', fontFamily: 'monospace' }}>{expanded ? '▲' : '▼'}</span>
      </div>
      <div style={{ fontSize: 12, color: '#a8d1ff', fontFamily: 'monospace', marginBottom: expanded ? 8 : 6 }}>
        {quest.shortDescription}
      </div>
      {expanded && (
        <div style={{ fontSize: 12, color: '#c0d8f0', fontFamily: 'monospace', marginBottom: 8, lineHeight: 1.5 }}>
          {quest.description}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#7799bb', fontFamily: 'monospace' }}>
          Given by: {quest.questGiverNpcName}
        </span>
        <span style={{ flex: 1 }} />
        {hasLocation ? (
          <>
            {cheatsEnabled && (
              <button
                onClick={(e) => { e.stopPropagation(); onJump(); }}
                title="Cheat: teleport here and trigger any encounter"
                style={cheatButtonStyle}
              >
                ⚡ Cheat: Jump To
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onNavigate(); }}
              style={{
                background: 'rgba(90, 160, 255, 0.15)',
                border: '1px solid #5aa0ff',
                borderRadius: 4,
                color: '#a8d1ff',
                fontFamily: 'monospace',
                fontSize: 11,
                padding: '3px 10px',
                cursor: 'pointer',
              }}
            >
              Navigate ▶
            </button>
          </>
        ) : (
          <span style={{ fontSize: 11, color: '#556677', fontFamily: 'monospace' }}>
            Location Unknown
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * A quest that is over, whether won or lost. Deliberately shares one component: neither
 * state offers Navigate (there is nowhere left to go), and a failed quest differs only in
 * its accent colour and in showing `outcomeNote` — the record of what was lost.
 */
const ClosedQuestEntry: React.FC<{
  quest: QuestDTO;
  expanded: boolean;
  onToggle: () => void;
}> = ({ quest, expanded, onToggle }) => {
  const failed = quest.status === 'failed';
  return (
    <div
      onClick={onToggle}
      style={{
        padding: '10px 12px',
        marginBottom: 8,
        background: failed ? 'rgba(34, 18, 20, 0.5)' : 'rgba(14, 24, 34, 0.5)',
        border: `1px solid ${failed ? '#3a2024' : '#1a2a3a'}`,
        borderRadius: 6,
        opacity: 0.8,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 'bold', fontSize: 13, color: failed ? '#aa8890' : '#8898aa', fontFamily: 'monospace', flex: 1 }}>
          {quest.title}
        </span>
        <span style={{ fontSize: 11, color: '#334455', fontFamily: 'monospace' }}>{expanded ? '▲' : '▼'}</span>
      </div>
      <div style={{ fontSize: 12, color: '#667788', fontFamily: 'monospace', marginBottom: expanded ? 8 : 6 }}>
        {quest.shortDescription}
      </div>
      {expanded && (
        <div style={{ fontSize: 12, color: '#7a8fa0', fontFamily: 'monospace', marginBottom: 8, lineHeight: 1.5 }}>
          {quest.description}
        </div>
      )}
      {failed && quest.outcomeNote && (
        <div style={{ fontSize: 12, color: '#bb8877', fontFamily: 'monospace', marginBottom: 6, lineHeight: 1.5 }}>
          {quest.outcomeNote}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#556677', fontFamily: 'monospace' }}>
          Given by: {quest.questGiverNpcName}
        </span>
        <span style={{ flex: 1 }} />
        {failed ? (
          <span style={{ fontSize: 11, color: '#996666', fontFamily: 'monospace' }}>Failed</span>
        ) : quest.completedDay != null && (
          <span style={{ fontSize: 11, color: '#556677', fontFamily: 'monospace' }}>
            Completed on Day {quest.completedDay}
          </span>
        )}
      </div>
    </div>
  );
};

export const QuestJournal: React.FC<Props> = ({ quests, onClose }) => {
  const [tab, setTab] = useState<Tab>('active');
  const [expandedQuestId, setExpandedQuestId] = useState<string | null>(null);
  const cheatsEnabled = useCheatStore((s) => s.cheatsEnabled);

  function handleToggle(questId: string) {
    setExpandedQuestId(prev => prev === questId ? null : questId);
  }

  const activeQuests = quests.filter(q => q.status === 'active' || q.status === 'ready_to_complete');
  const completedQuests = quests.filter(q => q.status === 'completed');
  const failedQuests = quests.filter(q => q.status === 'failed');

  return (
    <div className="quest-journal-overlay" onClick={onClose}>
      <div className="quest-journal-panel" onClick={(e) => e.stopPropagation()}>
        <button className="quest-journal-close" onClick={onClose}>✕</button>

        <div style={{
          fontSize: 16,
          fontWeight: 'bold',
          color: '#e8e8ff',
          fontFamily: 'monospace',
          marginBottom: 14,
          paddingRight: 30,
        }}>
          Quest Journal
        </div>

        {/* Tab bar */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #2a3a5a',
          marginBottom: 14,
          flexShrink: 0,
        }}>
          <TabButton active={tab === 'active'} onClick={() => setTab('active')}>
            Active {activeQuests.length > 0 && <span style={{ color: '#5aa0ff' }}>({activeQuests.length})</span>}
          </TabButton>
          <TabButton active={tab === 'completed'} onClick={() => setTab('completed')}>
            Completed {completedQuests.length > 0 && <span style={{ color: '#7799bb' }}>({completedQuests.length})</span>}
          </TabButton>
          {failedQuests.length > 0 && (
            <TabButton active={tab === 'failed'} onClick={() => setTab('failed')}>
              Failed <span style={{ color: '#bb7788' }}>({failedQuests.length})</span>
            </TabButton>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {tab === 'active' && (
            activeQuests.length === 0 ? (
              <div style={{ color: '#445566', fontFamily: 'monospace', fontSize: 13, textAlign: 'center', marginTop: 40 }}>
                No active quests.
              </div>
            ) : (
              activeQuests.map(quest => (
                <ActiveQuestEntry
                  key={quest.id}
                  quest={quest}
                  expanded={expandedQuestId === quest.id}
                  cheatsEnabled={cheatsEnabled}
                  onToggle={() => handleToggle(quest.id)}
                  onNavigate={() => {
                    const hex = quest.status === 'ready_to_complete' ? quest.questGiverHex : quest.targetHex;
                    if (hex) {
                      navigateToHex(hex.x, hex.y);
                      onClose();
                    }
                  }}
                  onJump={() => {
                    const hex = quest.status === 'ready_to_complete' ? quest.questGiverHex : quest.targetHex;
                    if (hex) {
                      jumpToHex(hex.x, hex.y);
                      onClose();
                    }
                  }}
                />
              ))
            )
          )}
          {tab === 'completed' && (
            completedQuests.length === 0 ? (
              <div style={{ color: '#445566', fontFamily: 'monospace', fontSize: 13, textAlign: 'center', marginTop: 40 }}>
                No completed quests.
              </div>
            ) : (
              completedQuests.map(quest => (
                <ClosedQuestEntry
                  key={quest.id}
                  quest={quest}
                  expanded={expandedQuestId === quest.id}
                  onToggle={() => handleToggle(quest.id)}
                />
              ))
            )
          )}
          {tab === 'failed' && (
            failedQuests.length === 0 ? (
              <div style={{ color: '#445566', fontFamily: 'monospace', fontSize: 13, textAlign: 'center', marginTop: 40 }}>
                No failed quests.
              </div>
            ) : (
              failedQuests.map(quest => (
                <ClosedQuestEntry
                  key={quest.id}
                  quest={quest}
                  expanded={expandedQuestId === quest.id}
                  onToggle={() => handleToggle(quest.id)}
                />
              ))
            )
          )}
        </div>
      </div>
    </div>
  );
};

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      padding: '6px 20px',
      background: 'none',
      border: 'none',
      borderBottom: active ? '2px solid #5aa0ff' : '2px solid transparent',
      color: active ? '#a8d1ff' : '#6699bb',
      cursor: 'pointer',
      fontFamily: 'monospace',
      fontSize: 13,
      marginBottom: -1,
    }}
  >
    {children}
  </button>
);
